import { platform } from './index';
import { nativeSecureCredential } from './nativeSecureCredential';

export const LEGACY_TOKEN_KEYS = Object.freeze(['token', 'accessToken', 'authToken', 'jwt']);
const NATIVE_LOGOUT_PENDING_KEY = 'z10:native_secure_logout_pending';

function readLegacyCredential(storage) {
  for (const key of LEGACY_TOKEN_KEYS) {
    const value = storage?.getItem?.(key);
    if (value) return value;
  }
  return null;
}

function clearLegacyCredential(storage) {
  LEGACY_TOKEN_KEYS.forEach((key) => storage?.removeItem?.(key));
}

export function createWebTokenStore(storage) {
  return Object.freeze({
    kind: 'web-local-storage',
    getAccessToken: () => readLegacyCredential(storage),
    loadAccessToken: async () => readLegacyCredential(storage),
    async setAccessToken(token) {
      if (!token) return false;
      storage?.setItem?.('token', token);
      storage?.setItem?.('accessToken', token);
      storage?.setItem?.('authToken', token);
      return true;
    },
    clearAccessTokenImmediate() {
      clearLegacyCredential(storage);
    },
    async clearAccessToken() {
      clearLegacyCredential(storage);
      return true;
    },
  });
}

export function createNativeTokenStore(storage, secureCredential = nativeSecureCredential) {
  let memoryCredential = null;
  let bootstrapPromise = null;

  const loadAccessToken = () => {
    if (bootstrapPromise) return bootstrapPromise;
    bootstrapPromise = (async () => {
      if (storage?.getItem?.(NATIVE_LOGOUT_PENDING_KEY)) {
        await secureCredential.removeCredential();
        storage?.removeItem?.(NATIVE_LOGOUT_PENDING_KEY);
        return null;
      }
      const secure = await secureCredential.getCredential();
      if (secure) {
        memoryCredential = secure;
        clearLegacyCredential(storage);
        return secure;
      }

      const legacy = readLegacyCredential(storage);
      if (!legacy) return null;

      // Migration is deliberately ordered: persist, verify, then remove the
      // WebView copy. A failed write/read leaves the legacy credential intact.
      await secureCredential.setCredential(legacy);
      const verified = await secureCredential.getCredential();
      if (verified !== legacy) throw new Error('secure_credential_verification_failed');
      memoryCredential = verified;
      clearLegacyCredential(storage);
      return verified;
    })();
    return bootstrapPromise;
  };

  return Object.freeze({
    kind: 'native-android-keystore',
    getAccessToken: () => memoryCredential,
    loadAccessToken,
    async setAccessToken(token) {
      if (!token) return false;
      await secureCredential.setCredential(token);
      const verified = await secureCredential.getCredential();
      if (verified !== token) throw new Error('secure_credential_verification_failed');
      memoryCredential = verified;
      clearLegacyCredential(storage);
      storage?.removeItem?.(NATIVE_LOGOUT_PENDING_KEY);
      return true;
    },
    clearAccessTokenImmediate() {
      memoryCredential = null;
      clearLegacyCredential(storage);
      storage?.setItem?.(NATIVE_LOGOUT_PENDING_KEY, '1');
    },
    async clearAccessToken() {
      memoryCredential = null;
      clearLegacyCredential(storage);
      storage?.setItem?.(NATIVE_LOGOUT_PENDING_KEY, '1');
      await secureCredential.removeCredential();
      storage?.removeItem?.(NATIVE_LOGOUT_PENDING_KEY);
      return true;
    },
  });
}

const browserStorage = typeof localStorage === 'undefined' ? null : localStorage;
export const tokenStore = platform.isNative
  ? createNativeTokenStore(browserStorage)
  : createWebTokenStore(browserStorage);
